import React, { useEffect, useState } from 'react';
import { vendorProfileAPI, uploadAPI, locationAPI } from '../../services/api';
import { FormSkeleton } from '../../components/skeletons';

/**
 * Vendor KYC — form + document uploads + submit-for-review workflow.
 * Lives at /vendor/kyc. Reads and patches the vendor's own profile via
 * `vendor-profiles/me`; submit-for-review is a separate endpoint that
 * validates + flips kyc.status to `submitted`.
 */

// ── Load Razorpay Checkout JS script dynamically ─────────────────────────────
const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (document.getElementById('razorpay-checkout-js')) { resolve(true); return; }
    const script = document.createElement('script');
    script.id = 'razorpay-checkout-js';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const S = {
  page: { padding: 24, background: 'var(--cream)', minHeight: '100vh' },
  h1: { fontFamily: 'var(--font-serif)', fontSize: 26, fontWeight: 700, color: 'var(--char)' },
  sub: { fontSize: 13, color: 'var(--muted)', marginBottom: 20, marginTop: 4 },
  banner: (bg, fg) => ({ background: bg, color: fg, borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 13, fontWeight: 600, lineHeight: 1.55 }),
  card: { background: '#fff', border: '1px solid var(--border)', borderRadius: 14, padding: 20, marginBottom: 16 },
  sec: { fontSize: 14, fontWeight: 700, color: 'var(--char)', marginBottom: 12, borderBottom: '1px dashed var(--border)', paddingBottom: 8 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14, marginBottom: 10 },
  label: { fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4, display: 'block' },
  input: { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, boxSizing: 'border-box' },
  btn: (variant = 'primary', disabled = false) => ({
    background: variant === 'primary' ? 'var(--green)' : 'transparent',
    color: variant === 'primary' ? '#fff' : 'var(--green)',
    border: variant === 'primary' ? 'none' : '1.5px solid var(--green)',
    padding: '10px 20px', borderRadius: 9, fontSize: 13, fontWeight: 700,
    cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
  }),
  upload: {
    border: '1.5px dashed var(--border)', borderRadius: 10, padding: 12, background: '#fafafa',
    display: 'flex', alignItems: 'center', gap: 10, fontSize: 13,
  },
  filePreview: { color: 'var(--green)', fontWeight: 700, textDecoration: 'none' },
};

const BUSINESS_TYPES = [
  { v: 'proprietorship', label: 'Proprietorship' },
  { v: 'partnership', label: 'Partnership' },
  { v: 'pvtLtd', label: 'Private Limited' },
  { v: 'llp', label: 'LLP' },
  { v: 'opc', label: 'OPC (One Person Company)' },
  { v: 'huf', label: 'HUF' },
  { v: 'trust', label: 'Trust / Society' },
  { v: 'other', label: 'Other' },
];

const FI_TYPES = [
  { v: 'bank', label: 'Scheduled Bank' },
  { v: 'nbfc', label: 'NBFC' },
  { v: 'hfc', label: 'Housing Finance Company' },
  { v: 'cooperative', label: 'Co-operative' },
  { v: 'other', label: 'Other' },
];

// Soft format checks — mirror the backend. These never block the form;
// they render as advisories so the vendor can self-correct if they like.
const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[A-Z0-9]{2}Z[A-Z0-9]{1}$/;
const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;
function computeWarnings(form, isFinancial) {
  const k = form.kyc || {};
  const w = [];
  if (!k.panNumber) w.push('PAN number is empty.');
  else if (!PAN_RE.test(String(k.panNumber).toUpperCase())) w.push('PAN format looks off — expected 5 letters, 4 digits, 1 letter (e.g. ABCDE1234F).');
  if (!k.panCardUrl) w.push('PAN card document not uploaded.');
  if (!k.addressProofUrl) w.push('Address proof not uploaded.');
  if (!k.businessType) w.push('Business type not selected.');
  if (!k.legalBusinessName) w.push('Legal business name is empty.');
  if (!k.bankAccountHolder || !k.bankAccountNumber || !k.bankIfsc) w.push('Bank account details are incomplete.');
  if (k.bankIfsc && !IFSC_RE.test(String(k.bankIfsc).toUpperCase())) w.push('IFSC format looks off — expected 4 letters, 0, 6 alphanumerics (e.g. HDFC0000123).');
  if (!k.cancelledChequeUrl) w.push('Cancelled cheque / bank statement not uploaded.');
  if (isFinancial) {
    if (!k.financialInstitutionType) w.push('Financial institution type not selected.');
    if (!k.rbiLicenseNumber) w.push('RBI license number is empty.');
    if (!k.rbiLicenseUrl) w.push('RBI license document not uploaded.');
  }
  return w;
}

function statusBanner(status, reason) {
  switch (status) {
    case 'verified':
      return { bg: '#E8F5EE', fg: '#1B5C3A', text: '✅ KYC verified — you are eligible to receive leads. Profile and KYC details are locked; only mobile number and password can still be changed. Contact support for other changes.' };
    case 'submitted':
      return { bg: '#FFF8E7', fg: '#8A5A00', text: '⏳ KYC submitted. Admin is reviewing your documents — typical turn-around 24–48 hours. You cannot edit the profile during review.' };
    case 'rejected':
      return { bg: '#FEE2E2', fg: '#B91C1C', text: `❌ KYC rejected${reason ? `: ${reason}` : '.'} Update the relevant fields and submit again.` };
    default:
      return { bg: '#FFF8E7', fg: '#8A5A00', text: '⚠️ KYC not submitted yet. Complete this form to start receiving leads.' };
  }
}

export default function Kyc() {
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(null);           // flattened editable shape
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(null); // which field is uploading
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  // Master list for state dropdown — populated once.
  useEffect(() => {
    locationAPI.states().then(r => setStates(r.data.data || [])).catch(() => { });
  }, []);

  useEffect(() => {
    vendorProfileAPI.me().then(r => {
      const p = r.data.data;
      if (!p) { setLoading(false); return; }
      setProfile(p);
      setForm({
        firmName: p.firmName || '',
        gstin: p.gstin || '',
        city: p.city || '',
        state: p.state || '',
        addressLine1: p.addressLine1 || '',
        addressLine2: p.addressLine2 || '',
        pincode: p.pincode || '',
        isGstRegistered: !!p.isGstRegistered,
        kyc: { ...(p.kyc || {}) },
      });
    }).catch(() => { }).finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setKyc = (k, v) => setForm(f => ({ ...f, kyc: { ...f.kyc, [k]: v } }));

  // Reload city list every time the selected state changes so only
  // master-listed cities are pickable.
  useEffect(() => {
    if (!form?.state) { setCities([]); return; }
    locationAPI.cities(form.state).then(r => setCities(r.data.data || [])).catch(() => setCities([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.state]);

  const uploadDoc = async (fieldName, file) => {
    setUploading(fieldName);
    try {
      const fd = new FormData();
      fd.append('files', file);
      const r = await uploadAPI.upload([file]);
      const url = r.data.data?.[0]?.url;
      if (url) setKyc(fieldName, url);
    } catch (e) {
      setError(e.response?.data?.message || 'Upload failed.');
    } finally { setUploading(null); }
  };

  const save = async () => {
    setSaving(true); setError('');
    try {
      const res = await vendorProfileAPI.update(form);
      setProfile(res.data.data);
      setToast('Saved.');
      setTimeout(() => setToast(''), 2500);
    } catch (e) {
      setError(e.response?.data?.message || 'Save failed.');
    } finally { setSaving(false); }
  };

  const isFeePaid = React.useMemo(() => {
    if (!profile?.kyc?.platformFeePaid || !profile?.kyc?.platformFeeExpiresAt) return false;
    return new Date(profile.kyc.platformFeeExpiresAt) > new Date();
  }, [profile]);

  const handlePayFee = async () => {
    try {
      // Ensure Razorpay script is loaded
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        setError('Failed to load Razorpay SDK. Please check your internet connection.');
        return;
      }

      const res = await vendorProfileAPI.createKycOrder();
      if (!res.data?.success) throw new Error(res.data?.message || 'Failed to create order');

      const orderData = res.data.data;
      if (orderData.testMode) {
        // Direct verify for test mode
        const v = await vendorProfileAPI.verifyKycPayment({ testMode: true });
        setProfile(v.data?.data);
        setToast('Annual KYC/Platform fee paid (Test Mode).');
        return;
      }

      const options = {
        key: orderData.keyId,
        amount: orderData.amountInPaise,
        currency: orderData.currency,
        name: 'Kanjusee',
        description: 'Annual KYC/Platform Fee',
        order_id: orderData.orderId,
        handler: async (response) => {
          try {
            const v = await vendorProfileAPI.verifyKycPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            setProfile(v.data?.data);
            setToast('Payment successful! Platform fee paid for 1 year.');
          } catch (e) {
            setError(e.response?.data?.message || 'Payment verification failed.');
          }
        },
        prefill: {
          name: orderData.vendorName || '',
          email: orderData.vendorEmail || '',
        },
        theme: { color: '#1B5C3A' },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    }
  };

  const submitForReview = async () => {
    const isLocalMode = profile?.systemMode === 'local_production';
    if (!isFeePaid && !isLocalMode) {
      const feeAmt = profile?.kyc?.platformFeeAmount || 500;
      setError(`Annual KYC/Platform fee (₹${feeAmt}) is required before submission.`);
      return;
    }
    setError('');

    // 1. Business Basics
    if (!form?.firmName || !String(form.firmName).trim()) {
      setError('Firm name is required in Business Basics.');
      return;
    }
    if (String(form.firmName).trim().length < 3 || String(form.firmName).trim().length > 100) {
      setError('Firm name must be between 3 and 100 characters.');
      return;
    }
    if (!form?.state || !String(form.state).trim()) {
      setError('State is required in Business Basics.');
      return;
    }
    if (!form?.city || !String(form.city).trim()) {
      setError('City is required in Business Basics.');
      return;
    }
    if (!form?.addressLine1 || !String(form.addressLine1).trim()) {
      setError('Address Line 1 is required in Business Basics.');
      return;
    }
    if (!form?.pincode || !String(form.pincode).trim()) {
      setError('Pincode is required in Business Basics.');
      return;
    }
    const PIN_RE_VAL = /^[1-9]\d{5}$/;
    if (!PIN_RE_VAL.test(String(form.pincode).trim())) {
      setError('Invalid Pincode format. (Expected: 6-digit Indian Pincode)');
      return;
    }

    // 2. Identity details
    const panNumber = form?.kyc?.panNumber;
    if (!panNumber || !String(panNumber).trim()) {
      setError('PAN number is required in Identity Details.');
      return;
    }
    const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
    if (!PAN_RE.test(String(panNumber).trim().toUpperCase())) {
      setError('Invalid PAN number format. (Expected format: 5 letters, 4 digits, 1 letter)');
      return;
    }

    // 3. Business Registration
    const businessType = form?.kyc?.businessType;
    const VALID_BUSINESS_TYPES = ['proprietorship', 'partnership', 'pvtLtd', 'llp', 'opc', 'huf', 'trust', 'other'];
    if (!businessType || !VALID_BUSINESS_TYPES.includes(businessType)) {
      setError('Valid Business Type is required in Business Registration.');
      return;
    }
    if (!form?.kyc?.legalBusinessName || !String(form.kyc.legalBusinessName).trim()) {
      setError('Legal business name is required in Business Registration.');
      return;
    }

    // Save before submitting so validation runs against the latest data.
    setSubmitting(true);
    try {
      await vendorProfileAPI.update(form);
      const r = await vendorProfileAPI.submitKyc();
      setProfile(r.data.data);
      setToast('KYC submitted for review.');
      setTimeout(() => setToast(''), 3000);
    } catch (e) {
      setError(e.response?.data?.message || 'Submission failed.');
    } finally { setSubmitting(false); }
  };

  if (loading) return <div style={{ ...S.page, display: 'flex', justifyContent: 'center' }}>Loading…</div>;

  if (!profile) return (
    <div style={S.page}>
      <div style={S.h1}>KYC</div>
      <p style={S.sub}>You don't have a vendor profile yet. Complete vendor onboarding first to begin KYC.</p>
    </div>
  );

  const status = profile.kyc?.status || 'draft';
  const reason = profile.kyc?.rejectionReason || '';
  const locked = status === 'submitted' || status === 'verified';
  const banner = statusBanner(status, reason);
  const isFinancial = profile.vendorType === 'financial';

  const DocField = ({ fieldName, label, accept = 'image/*,.pdf' }) => {
    const url = form.kyc[fieldName];
    const inputId = `kj-file-${fieldName}`;
    return (
      <div>
        <label style={S.label}>{label}</label>
        <div style={S.upload}>
          {url ? (
            <>
              <a href={url} target="_blank" rel="noopener noreferrer" style={S.filePreview}>📎 View uploaded</a>
              {!locked && (
                <>
                  <span style={{ color: 'var(--muted)' }}>·</span>
                  <label htmlFor={inputId} style={{ color: 'var(--green)', cursor: 'pointer', fontWeight: 600 }}>
                    Replace
                  </label>
                </>
              )}
            </>
          ) : (
            <>
              <label htmlFor={inputId} style={{ color: 'var(--green)', cursor: 'pointer', fontWeight: 700 }}>
                {uploading === fieldName ? 'Uploading…' : '+ Upload (PDF or image)'}
              </label>
              <span style={{ color: 'var(--muted)', fontSize: 11 }}>max 50 MB</span>
            </>
          )}
          <input
            id={inputId}
            type="file"
            accept={accept}
            style={{ display: 'none' }}
            disabled={locked || uploading === fieldName}
            onChange={e => e.target.files?.[0] && uploadDoc(fieldName, e.target.files[0])}
          />
        </div>
      </div>
    );
  };

  if (loading) {
    return <FormSkeleton fields={6} style={{ margin: '24px 20px' }} />;
  }

  return (
    <div style={S.page}>
      <div style={S.h1}>KYC & Verification</div>
      <p style={S.sub}>
        We verify every vendor before they can receive buyer demands. Complete the form, upload the
        documents, and submit for admin review. Expected turn-around: 24–48 hours.
      </p>

      <div style={S.banner(banner.bg, banner.fg)}>{banner.text}</div>

      {/* Platform Fee Banner */}
      {profile?.systemMode !== 'local_production' && (
        !isFeePaid ? (
          <div style={{ ...S.banner('#FFF8E7', '#8A5A00'), border: '1.5px solid #EAC475', padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 20 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>💳 Annual KYC & Platform Fee Required</div>
                <div style={{ fontSize: 13, opacity: 0.9 }}>
                  A one-time annual fee of ₹{(profile?.kyc?.platformFeeAmount || 500).toLocaleString('en-IN')} is required to submit your KYC and receive buyer leads.
                  It is valid for 365 days from the date of payment.
                </div>
              </div>
              <button style={{ ...S.btn('primary'), padding: '12px 24px', fontSize: 14 }} onClick={handlePayFee}>
                Pay ₹{(profile?.kyc?.platformFeeAmount || 500).toLocaleString('en-IN')} Now
              </button>
            </div>
          </div>
        ) : (
          <div style={{ ...S.banner('#E8F5EE', '#1B5C3A'), border: '1.5px solid #A7F3D0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 18 }}>✅</span>
              <span>
                <strong>Annual Platform Fee Paid.</strong> Your subscription is active and valid until
                <strong> {new Date(profile.kyc.platformFeeExpiresAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>.
              </span>
            </div>
          </div>
        )
      )}

      {error && <div style={S.banner('#FEE2E2', '#B91C1C')}>{error}</div>}
      {toast && <div style={S.banner('#E8F5EE', '#1B5C3A')}>{toast}</div>}

      {/* Basic profile */}
      <div style={S.card}>
        <div style={S.sec}>Business basics</div>
        <div style={S.grid}>
          <div><label style={S.label}>Firm name *</label><input style={S.input} disabled={locked} value={form.firmName} onChange={e => set('firmName', e.target.value)} /></div>
          <div><label style={S.label}>GSTIN</label><input style={S.input} disabled={locked} value={form.gstin} onChange={e => set('gstin', e.target.value)} placeholder="24XXXXXXXXXXXX1Z5" /></div>
          <div>
            <label style={S.label}>State *</label>
            <select style={{ ...S.input, cursor: locked ? 'not-allowed' : 'pointer' }} disabled={locked}
              value={form.state}
              onChange={e => setForm(f => ({ ...f, state: e.target.value, city: '' }))}>
              <option value="">— Select state —</option>
              {states.map(s => <option key={s.state || s} value={s.state || s}>{s.state || s}</option>)}
            </select>
          </div>
          <div>
            <label style={S.label}>City *</label>
            <select style={{ ...S.input, cursor: locked || !form.state ? 'not-allowed' : 'pointer' }} disabled={locked || !form.state}
              value={form.city}
              onChange={e => set('city', e.target.value)}>
              <option value="">{form.state ? '— Select city —' : 'Pick a state first'}</option>
              {cities.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div><label style={S.label}>Address line 1</label><input style={S.input} disabled={locked} value={form.addressLine1} onChange={e => set('addressLine1', e.target.value)} /></div>
          <div><label style={S.label}>Address line 2</label><input style={S.input} disabled={locked} value={form.addressLine2} onChange={e => set('addressLine2', e.target.value)} /></div>
          <div><label style={S.label}>Pincode</label><input style={S.input} disabled={locked} value={form.pincode} onChange={e => set('pincode', e.target.value)} /></div>
        </div>
      </div>

      {/* Identity */}
      <div style={S.card}>
        <div style={S.sec}>Identity (proprietor / authorised signatory)</div>
        <div style={S.grid}>
          <div><label style={S.label}>PAN number *</label><input style={S.input} disabled={locked} value={form.kyc.panNumber || ''} onChange={e => setKyc('panNumber', e.target.value)} placeholder="ABCDE1234F" /></div>
          <div><label style={S.label}>Aadhaar (last 4 digits)</label><input style={S.input} disabled={locked} value={form.kyc.aadhaarLast4 || ''} onChange={e => setKyc('aadhaarLast4', e.target.value)} placeholder="1234" /></div>
        </div>
        <div style={S.grid}>
          <DocField fieldName="panCardUrl" label="PAN card (image or PDF) *" />
          <DocField fieldName="addressProofUrl" label="Address proof (utility bill / lease) *" />
        </div>
      </div>

      {/* Business registration */}
      <div style={S.card}>
        <div style={S.sec}>Business registration</div>
        <div style={S.grid}>
          <div>
            <label style={S.label}>Business type *</label>
            <select style={S.input} disabled={locked} value={form.kyc.businessType || ''} onChange={e => setKyc('businessType', e.target.value)}>
              <option value="">— Select —</option>
              {BUSINESS_TYPES.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
          </div>
          <div><label style={S.label}>Legal business name *</label><input style={S.input} disabled={locked} value={form.kyc.legalBusinessName || ''} onChange={e => setKyc('legalBusinessName', e.target.value)} /></div>
          <div><label style={S.label}>Business registration no. (CIN / LLPIN)</label><input style={S.input} disabled={locked} value={form.kyc.businessRegistrationNo || ''} onChange={e => setKyc('businessRegistrationNo', e.target.value)} /></div>
          <div><label style={S.label}>Business PAN (for companies / LLP)</label><input style={S.input} disabled={locked} value={form.kyc.businessPanNumber || ''} onChange={e => setKyc('businessPanNumber', e.target.value)} placeholder="ABCDE1234F" /></div>
        </div>
        <div style={S.grid}>
          <DocField fieldName="gstCertificateUrl" label="GST certificate (if registered)" />
        </div>
      </div>

      {/* Bank */}
      <div style={S.card}>
        <div style={S.sec}>Bank account (for payouts and verification)</div>
        <div style={S.grid}>
          <div><label style={S.label}>Account holder name *</label><input style={S.input} disabled={locked} value={form.kyc.bankAccountHolder || ''} onChange={e => setKyc('bankAccountHolder', e.target.value)} /></div>
          <div><label style={S.label}>Bank name</label><input style={S.input} disabled={locked} value={form.kyc.bankName || ''} onChange={e => setKyc('bankName', e.target.value)} /></div>
          <div><label style={S.label}>Account number *</label><input style={S.input} disabled={locked} value={form.kyc.bankAccountNumber || ''} onChange={e => setKyc('bankAccountNumber', e.target.value)} /></div>
          <div><label style={S.label}>IFSC *</label><input style={S.input} disabled={locked} value={form.kyc.bankIfsc || ''} onChange={e => setKyc('bankIfsc', e.target.value)} placeholder="HDFC0000123" /></div>
        </div>
        <div style={S.grid}>
          <DocField fieldName="cancelledChequeUrl" label="Cancelled cheque / bank statement *" />
        </div>
      </div>

      {/* Financial-vendor only */}
      {isFinancial && (
        <div style={S.card}>
          <div style={S.sec}>🏦 Financial institution details</div>
          <div style={S.grid}>
            <div>
              <label style={S.label}>Institution type *</label>
              <select style={S.input} disabled={locked} value={form.kyc.financialInstitutionType || ''} onChange={e => setKyc('financialInstitutionType', e.target.value)}>
                <option value="">— Select —</option>
                {FI_TYPES.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
              </select>
            </div>
            <div><label style={S.label}>RBI license number *</label><input style={S.input} disabled={locked} value={form.kyc.rbiLicenseNumber || ''} onChange={e => setKyc('rbiLicenseNumber', e.target.value)} /></div>
          </div>
          <div style={S.grid}>
            <DocField fieldName="rbiLicenseUrl" label="RBI license document *" />
          </div>
        </div>
      )}

      {/* Soft warnings — never block submission. Vendors can still send the
          application with gaps; admin reviews and decides. */}
      {!locked && (() => {
        const warnings = computeWarnings(form, isFinancial);
        if (!warnings.length) return null;
        return (
          <div style={{ background: '#FFF8E7', border: '1px solid #EAC475', color: '#8A5A00', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 13, lineHeight: 1.55 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>⚠ Please review — these items look incomplete or off-format:</div>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {warnings.map((w, i) => <li key={i} style={{ marginBottom: 2 }}>{w}</li>)}
            </ul>
            <div style={{ fontSize: 12, marginTop: 8, fontStyle: 'italic' }}>You can still submit — admin will review and flag any genuine issues.</div>
          </div>
        );
      })()}

      {/* Any server-captured warnings from the last submission. */}
      {profile.kyc?.warnings?.length > 0 && status !== 'verified' && (
        <div style={{ background: '#FFF8E7', border: '1px solid #EAC475', color: '#8A5A00', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 13, lineHeight: 1.55 }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>ℹ Notes flagged at your last submission:</div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {profile.kyc.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}

      {!locked && (
        <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
          <button style={S.btn('outline', saving)} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save draft'}</button>
          <button style={S.btn('primary', submitting)} onClick={submitForReview} disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit for review →'}
          </button>
        </div>
      )}
    </div>
  );
}